import { useRef, useContext, useState, useMemo, useEffect } from "react";
import Loader from "../../utils/Loading";
import styles from "./styles.module.css";
import CustomerContext from "context/CustomerContext";
import { TextField, InputAdornment, IconButton, MenuItem } from "@mui/material";
import { customerAmountFormat } from "helpers/NumberFormatHelper";
import SearchIcon from "images/svg/search.svg";
import ShopContext from "context/ShopContext";
import Button from "@mui/material/Button";
import toast from "react-hot-toast";
import CustomFormSwitch from "../CustomFields/CustomFormSwitch";
import { CREDIT_LIMIT_TYPES } from "const";

const colors = ["#FFB6C1", "#FFD700", "#90EE90", "#87CEFA", "#FFA07A", "#DDA0DD", "#F4A460", "#FF69B4"];

function CustomerSelector({ open, onClose, onSelect, style }) {
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  const { customers, loading, addCustomer } = useContext(CustomerContext);
  const { selectedShop } = useContext(ShopContext);

  const [searchText, setSearchText] = useState("");
  const [isAdding, setIsAdding] = useState(false);

  const [newCustomer, setNewCustomer] = useState({
    name: "",
    mobile: "",
    location: "",
    gst: "",
    credit_limit: "",
    balance: "",
    credit_support: false,
    credit_limit_type: CREDIT_LIMIT_TYPES.CREDIT,
    balance_type: CREDIT_LIMIT_TYPES.CREDIT,
  });

  useEffect(() => {
    if (!open) return;
    if (!searchInputRef.current) return;
    searchInputRef.current.focus();
  }, [open]);

  const getInitials = (name = "") => {
    const parts = name.trim().split(" ");
    if (parts.length === 1) {
      const word = parts[0];
      if (word.length >= 2) return (word[0] + word[1]).toUpperCase();
      return word[0]?.toUpperCase() || "";
    }
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const filteredCustomers = useMemo(() => {
    const list = customers;

    if (!Array.isArray(list)) return [];

    return list.filter(
      (c) => c.name?.toLowerCase().includes(searchText.toLowerCase()) || c.mobile?.toString().includes(searchText),
    );
  }, [customers, searchText]);

  const handleCreateCustomer = async () => {
    if (!newCustomer.name || !newCustomer.mobile) {
      return toast.error("Name and Mobile are required");
    }

    if (newCustomer.mobile?.length < 8 || newCustomer.mobile?.length > 12) {
      return toast.error("Mobile number is not valid");
    }

    setIsAdding(true);
    try {
      const limitAmt = Number(newCustomer.credit_limit) || 0;
      const adjustedLimit =
        newCustomer.credit_limit_type === CREDIT_LIMIT_TYPES.CREDIT ? -Math.abs(limitAmt) : Math.abs(limitAmt);

      const balAmt = Number(newCustomer.balance) || 0;
      const adjustedBalance =
        newCustomer.balance_type === CREDIT_LIMIT_TYPES.CREDIT ? -Math.abs(balAmt) : Math.abs(balAmt);

      const payload = {
        ...newCustomer,
        credit_limit: adjustedLimit,
        balance: adjustedBalance,
      };

      const response = await addCustomer(payload);

      onSelect(response);
      setSearchText("");
      onClose();
    } catch (error) {
      toast.error(error || "Failed to create customer");
    } finally {
      setIsAdding(false);
    }
  };

  useEffect(() => {
    if (filteredCustomers.length === 0 && searchText.trim() !== "") {
      const isNumeric = /^\d+$/.test(searchText.trim());
      if (isNumeric) {
        setNewCustomer((prev) => ({ ...prev, mobile: searchText.trim(), name: "" }));
      } else {
        setNewCustomer((prev) => ({ ...prev, name: searchText, mobile: "" }));
      }
    }
  }, [filteredCustomers.length, searchText]);

  if (!open) return null;

  return (
    <div className={styles.dropdownWrapper} style={style} ref={dropdownRef}>
      <Loader isOpen={loading || isAdding} />

      <div className={styles.list}>
        {filteredCustomers.length > 0
          ? filteredCustomers.map((c, index) => (
              <div key={c.id ?? c._id} className={styles.row} onClick={() => onSelect(c)}>
                <div className={styles.nameIcon} style={{ backgroundColor: colors[index % colors.length] }}>
                  {getInitials(c.name)}
                </div>
                <div>
                  <h3 className={styles.custName}>{c.name}</h3>
                  <div className={styles.subContent}>
                    <h3>{c.mobile}</h3>
                    <div className={styles.bar} />
                    <h3>
                      Balance: <span className={styles.rupeeIcon}>{selectedShop?.currency_symbol}</span>
                      {customerAmountFormat(c.balance)}
                    </h3>
                  </div>
                </div>
              </div>
            ))
          : !loading && (
              <div className={styles.addCustomerForm}>
                <h4>No customer found. Add new?</h4>
                <TextField
                  label="Name"
                  fullWidth
                  margin="dense"
                  size="small"
                  value={newCustomer.name}
                  onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                />
                <TextField
                  label="Mobile"
                  fullWidth
                  margin="dense"
                  size="small"
                  value={newCustomer.mobile}
                  onChange={(e) => setNewCustomer({ ...newCustomer, mobile: e.target.value })}
                />
                <TextField
                  label="Address"
                  fullWidth
                  margin="dense"
                  size="small"
                  value={newCustomer.location}
                  onChange={(e) => setNewCustomer({ ...newCustomer, location: e.target.value })}
                />
                <TextField
                  label="GST"
                  fullWidth
                  margin="dense"
                  size="small"
                  value={newCustomer.gst}
                  onChange={(e) => setNewCustomer({ ...newCustomer, gst: e.target.value })}
                />

                <div className={styles.creditSupportContainer}>
                  <h4 className={styles.creditSupportLabel}>Credit Support</h4>
                  <CustomFormSwitch
                    checked={newCustomer.credit_support}
                    onChange={() => setNewCustomer({ ...newCustomer, credit_support: !newCustomer.credit_support })}
                    sx={{ transform: "scale(0.7)", transformOrigin: "right center" }}
                  />
                </div>

                {newCustomer.credit_support && (
                  <>
                    <div className={styles.typeAmountContainer}>
                      <TextField
                        select
                        label="Credit Type"
                        size="small"
                        fullWidth
                        className={styles.typeSelector}
                        value={newCustomer.credit_limit_type}
                        onChange={(e) => setNewCustomer({ ...newCustomer, credit_limit_type: e.target.value })}
                        onClick={(e) => e.stopPropagation()}
                        SelectProps={{
                          MenuProps: {
                            disablePortal: true,
                          },
                        }}
                        sx={{
                          "& .MuiSelect-select": {
                            color: newCustomer.credit_limit_type === CREDIT_LIMIT_TYPES.CREDIT ? "red" : "green",
                          },
                        }}
                      >
                        <MenuItem value={CREDIT_LIMIT_TYPES.CREDIT}>Credit</MenuItem>
                        <MenuItem value={CREDIT_LIMIT_TYPES.ADVANCE}>Advance</MenuItem>
                      </TextField>

                      <TextField
                        label="Credit Limit"
                        fullWidth
                        size="small"
                        className={styles.amountField}
                        value={newCustomer.credit_limit}
                        onChange={(e) => setNewCustomer({ ...newCustomer, credit_limit: e.target.value })}
                      />
                    </div>

                    <div className={styles.typeAmountContainer}>
                      <TextField
                        select
                        label="Balance Type"
                        size="small"
                        fullWidth
                        className={styles.typeSelector}
                        value={newCustomer.balance_type}
                        onChange={(e) => setNewCustomer({ ...newCustomer, balance_type: e.target.value })}
                        onClick={(e) => e.stopPropagation()}
                        SelectProps={{
                          MenuProps: {
                            disablePortal: true,
                          },
                        }}
                        sx={{
                          "& .MuiSelect-select": {
                            color: newCustomer.balance_type === CREDIT_LIMIT_TYPES.CREDIT ? "red" : "green",
                          },
                        }}
                      >
                        <MenuItem value={CREDIT_LIMIT_TYPES.CREDIT}>Credit</MenuItem>
                        <MenuItem value={CREDIT_LIMIT_TYPES.ADVANCE}>Advance</MenuItem>
                      </TextField>

                      <TextField
                        label="Balance"
                        fullWidth
                        size="small"
                        className={styles.amountField}
                        value={newCustomer.balance}
                        onChange={(e) => setNewCustomer({ ...newCustomer, balance: e.target.value })}
                      />
                    </div>
                  </>
                )}

                <Button fullWidth className={styles.redBtn} onClick={handleCreateCustomer}>
                  Create Customer
                </Button>
              </div>
            )}
      </div>

      {/* Search Section */}
      <div className={styles.searchWrapper}>
        <TextField
          inputRef={searchInputRef}
          placeholder="Search"
          fullWidth
          size="small"
          className={styles.searchField}
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="end">
                <IconButton edge="end">
                  <img src={SearchIcon} alt="Search" className={styles.icon} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </div>
    </div>
  );
}

export default CustomerSelector;
